import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Eye, ThumbsUp, MessageSquare, ThumbsDown } from "react-feather";
import {
  ListGroup,
  ListGroupItem,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";

import Loader from "../../../Shared/Loader";
import DropboxPlayer from "../../../Shared/DropboxPlayer";
import { addDefaultSrc, abbreviateNumber } from "../../../utilities";
import GenerateTiktokThumbnail from "../../../Shared/GenerateTiktokThumbnail";
import useIntersectionObserver from "../../../Shared/Hooks/useIntersectionObserver";

const renderProducts = (products) => {
  if (products?.length > 0) {
    return products.map(({ _id, name }, index) => (
      <Link
        key={_id}
        to={`/products/${_id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {name}
        {index < products.length - 1 && ", "}
      </Link>
    ));
  }
  return "";
};

const VideoItem = ({
  video,
  selectedVideos,
  onCheck,
  products,
  isAppManager,
}) => (
  <ListGroupItem>
    <Row>
      <Col lg={"4"}>
        {video.platform === "Dropbox" && video.video_url && (
          <div className="videoWrapper">
            <DropboxPlayer
              styleClass={"videoFrame"}
              videoUrl={video.video_url}
            />
          </div>
        )}
        {video.platform === "Youtube" && video.thumbnail_url && (
          <div className="text-center">
            <img
              src={video && video.thumbnail_url}
              alt={video.title}
              onError={(e) => addDefaultSrc(e)}
              width="auto"
              height={165}
            />
          </div>
        )}
        {video.platform === "Tiktok" && video.thumbnail_url && (
          <GenerateTiktokThumbnail
            video={video}
            customStyle={{ background: "black" }}
            imgStyle={{ objectFit: "cover", maxHeight: "150px" }}
          />
        )}
      </Col>
      <Col lg={"7"}>
        <div className="VideoItem-Content">
          <div className="video-detail-li mt-1">
            <b>Title:</b>{" "}
            <Link
              to={`${isAppManager ? "/published/videos/" : "/videos/"}${
                video._id
              }`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {video.title}
            </Link>
          </div>
          <div className="video-detail-li mt-1">
            <b>Creator:</b>{" "}
            <Link
              to={`/influencers/${video.video_author_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {video.author}
            </Link>
          </div>
          <div className="video-detail-li mt-1">
            <b>Product(s):</b> {renderProducts(products)}
          </div>
          <div className="video-detail-li mt-1">
            <div className="VideoItem-Quantified">
              <span className="VideoItem-Quantity">
                <Eye size={16} /> <b>Views:</b>{" "}
                {video.platform === "Youtube" && video.youtube_view_count
                  ? abbreviateNumber(video.youtube_view_count)
                  : null}
                {video.platform === "Tiktok" && video.view_count
                  ? abbreviateNumber(video.view_count)
                  : null}
              </span>
              <span className="VideoItem-Quantity">
                <MessageSquare size={16} /> <b>Comments:</b>{" "}
                {video.comment_count
                  ? abbreviateNumber(video.comment_count)
                  : 0}
              </span>
              <span className="VideoItem-Quantity">
                <ThumbsUp size={16} /> <b>Likes:</b>{" "}
                {video.likes ? abbreviateNumber(video.likes) : 0}
              </span>
              {video.platform === "Youtube" && (
                <span className="VideoItem-Quantity">
                  <ThumbsDown size={16} /> <b>Dislikes:</b>{" "}
                  {video.youtube_dislike_count
                    ? abbreviateNumber(video.youtube_dislike_count)
                    : 0}
                </span>
              )}
            </div>
          </div>
        </div>
      </Col>
      <Col lg={"1"} className="align-items-end align-top primary-checkbox">
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              checked={selectedVideos.indexOf(video._id) !== -1}
              onChange={onCheck(video._id)}
              name="selectVideo"
              color="primary"
            />
          </Label>
        </FormGroup>
      </Col>
    </Row>
  </ListGroupItem>
);

const VideoList = (props) => {
  const { videos, selectedVideos, onCheck, hasMore, getMore, loading } = props;
  const ref = useRef();
  const onScreen = useIntersectionObserver(ref);

  useEffect(() => {
    if (hasMore && onScreen) getMore();
  }, [onScreen]);

  const location = useLocation();
  const isAppManager = location.pathname === "/published";

  return (
    <PerfectScrollbar
      className="main-menu-content pe-1"
      options={{ wheelPropagation: true }}
      style={{ maxHeight: "450px", minScrollbarLength: 0 }}
    >
      <ListGroup className="VideoList">
        {videos &&
          videos.length > 0 &&
          videos.map(({ video, products }) => (
            <VideoItem
              key={video._id}
              video={video}
              selectedVideos={selectedVideos}
              onCheck={onCheck}
              products={products}
              isAppManager={isAppManager}
            />
          ))}
        <div ref={ref} className="VideoList-Loader">
          {loading && <Loader />}
        </div>
      </ListGroup>
    </PerfectScrollbar>
  );
};

VideoList.defaultProps = {
  videos: [],
  videoProducts: [],
  selectedVideos: [],
  hasMore: false,
  loading: false,
};

VideoList.propTypes = {
  hasMore: PropTypes.bool,
  loading: PropTypes.bool,
  getMore: PropTypes.func.isRequired,
  onCheck: PropTypes.func.isRequired,
  videos: PropTypes.arrayOf(PropTypes.object),
  videoProducts: PropTypes.arrayOf(PropTypes.array),
  selectedVideos: PropTypes.arrayOf(PropTypes.string),
};

export default VideoList;
